import {
  Chip,
  DataTableMeta,
  DateColumn,
  LimitedContent,
  Typography,
} from '@otp/ui';
import { TFunction } from 'i18next';
import React from 'react';

import { User, WttpQuestionnaireQuestion } from 'src/graphql/generated/graphql';

type ImportanceValue = 'Low' | 'Medium' | 'High';

const colorMapping: Record<ImportanceValue, 'default' | 'info' | 'error'> = {
  Low: 'default',
  Medium: 'info',
  High: 'error',
};

export type WttpQuestionsData = Omit<
  WttpQuestionnaireQuestion,
  'createdByUser'
> & {
  createdByUser?: Pick<User, 'firstName' | 'lastName'> | null;
};

export const getTableConfig = (
  wttpQuestions: WttpQuestionsData[],
  { t }: { t: TFunction },
) => [
  {
    name: 'order',
    label: t('wttp.questionnaire.list.order-number'),
    options: {
      customBodyRender: (value: any, column: DataTableMeta) => {
        return wttpQuestions[column.rowIndex].order;
      },
    },
  },
  {
    name: 'category',
    label: t('wttp.questionnaire.list.category'),
    options: {
      customBodyRender: (value: any, column: DataTableMeta) => {
        const rowData = wttpQuestions[column.rowIndex];
        return (
          <Chip label={rowData.category?.name} color="primary" size="small" />
        );
      },
    },
  },
  {
    name: 'title',
    label: t('wttp.questionnaire.list.question-title'),
    options: {
      customBodyRender: (value: string) => {
        return (
          <Typography variant="subtitle1" fontWeight="bold">
            {value}
          </Typography>
        );
      },
    },
  },
  {
    name: 'description',
    label: t('wttp.questionnaire.list.description'),
    options: {
      customBodyRender: (value: string) => {
        return (
          <LimitedContent maxRows={2}>
            <div dangerouslySetInnerHTML={{ __html: value }} />
          </LimitedContent>
        );
      },
    },
  },
  {
    name: 'updatedAt',
    label: t('common.updated-at'),
    options: {
      customBodyRender: (value: string, column: DataTableMeta) => {
        const rowData = wttpQuestions[column.rowIndex];
        return <DateColumn date={value || rowData.createdAt} />;
      },
      isDesktopOnly: true,
    },
  },
  {
    name: 'importance',
    label: t('wttp.questionnaire.list.importance'),
    options: {
      customBodyRender: (value: ImportanceValue) => {
        const mappedColor = colorMapping[value];
        return <Chip label={value} color={mappedColor} size="small" />;
      },
    },
  },
];
